import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { purchaseComparisonsApi as api } from "../../apis/purchaseComparisons";
import {
  activePurchaseMenus, configuredPurchaseMenus, hasPurchaseRight, offerKey,
  purchaseDocumentLocation, readPurchaseMenus, validatePurchaseSelections, validatePurchaseSettings,
} from "./purchaseComparisonModel";
import "./PurchaseComparisonsPage.css";

const text = (value) => value === null || value === undefined || value === "" ? "—" : typeof value === "object" ? JSON.stringify(value) : String(value);
const label = (key) => key.replace(/_/g, " ");
const money = (value) => Number.isFinite(value) ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "Unavailable";

function SourceData({ title, data }) {
  return <details className="pqc-source"><summary>{title}</summary><dl>{Object.entries(data || {}).map(([key, value]) =>
    <div key={key}><dt>{label(key)}</dt><dd>{text(value)}</dd></div>)}</dl></details>;
}

function DocumentLink({ menus, code, id, children }) {
  const destination = purchaseDocumentLocation(menus, code, id);
  return destination ? <Link to={destination.pathname} state={destination.state}>{children}</Link> : <span>{children} (view link unavailable)</span>;
}

function Pager({ page, total, size = 20, onChange, disabled }) {
  return <nav className="pqc-actions" aria-label="Pagination">
    <button type="button" disabled={disabled || page <= 1} onClick={() => onChange(page - 1)}>Previous</button>
    <span>Page {page} of {Math.max(1, Math.ceil(total / size))}</span>
    <button type="button" disabled={disabled || page * size >= total} onClick={() => onChange(page + 1)}>Next</button>
  </nav>;
}

function History({ onOpen, selectedID, refresh, disabled }) {
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setResult(null); setError("");
    api.list(page, controller.signal).then((data) => { if (!controller.signal.aborted) setResult(data); })
      .catch((e) => { if (!controller.signal.aborted) setError(e.message); });
    return () => controller.abort();
  }, [page, retry, refresh]);
  return <section className="pqc-panel" aria-label="Comparison history">
    <div className="pqc-heading"><h2>Comparison history</h2><button disabled={disabled} onClick={() => setRetry((n) => n + 1)}>Refresh history</button></div>
    {error ? <p role="alert">{error}</p> : !result ? <p role="status">Loading history…</p> : <>
      {!result.data.length ? <p>No saved comparisons on this page.</p> : <ul className="pqc-history">{result.data.map((row) => <li key={row.id}>
        <button disabled={disabled} aria-current={String(row.id) === selectedID ? "true" : undefined} onClick={() => onOpen(row.id)}>
          <strong>Comparison #{row.id}</strong><span>{row.indent_code} #{row.indent_id} → {row.order_code}</span>
          <span className="pqc-badge">{row.status}</span><small>{row.created_at ? new Date(row.created_at).toLocaleString() : ""}</small>
        </button>
      </li>)}</ul>}
      <Pager page={page} total={result.total} onChange={setPage} disabled={disabled} />
    </>}
  </section>;
}

const codeFields = [["indent_code", "Indent transaction"], ["enquiry_code", "Enquiry transaction"], ["quotation_code", "Vendor quotation transaction"], ["order_code", "Purchase order transaction"]];

function CreateComparison({ menus, onCreated, onBusy }) {
  const choices = useMemo(() => configuredPurchaseMenus(menus), [menus]);
  const [codes, setCodes] = useState({ indent_code: "", enquiry_code: "", quotation_code: "", order_code: "" });
  const [indentID, setIndentID] = useState("");
  const [indents, setIndents] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [retry, setRetry] = useState(0);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const indentCode = codes.indent_code;
  const canReadIndent = hasPurchaseRight(menus, indentCode, "can_view");
  useEffect(() => {
    const controller = new AbortController();
    setIndents(null); setIndentID(""); setLoadError(""); setPage(1); setSearch("");
    if (indentCode && canReadIndent) {
      api.indents(indentCode, controller.signal).then((data) => {
        if (controller.signal.aborted) return;
        validatePurchaseSettings(data.TransactionSettings, indentCode);
        setIndents(data.TransactionList);
      }).catch((e) => { if (!controller.signal.aborted) setLoadError(e.message); });
    }
    return () => controller.abort();
  }, [indentCode, canReadIndent, retry]);
  const filtered = (indents || []).filter((row) => Object.values(row).some((value) => text(value).toLowerCase().includes(search.toLowerCase())));
  const selected = (indents || []).find((row) => String(row.tran_id) === indentID);
  const allowed = codeFields.every(([key]) => choices.some((entry) => entry.menu_code === codes[key]) &&
    (key === "order_code" || hasPurchaseRight(menus, codes[key], "can_view")));

  async function create(event) {
    event.preventDefault();
    if (lock.current || !allowed || !selected) return;
    if (new Set(Object.values(codes)).size !== 4) { setError("Choose four distinct configured transaction codes."); return; }
    lock.current = true; setBusy(true); onBusy(true); setError("");
    try {
      const settings = await Promise.all(codeFields.map(([key]) => api.settings(codes[key])));
      settings.forEach((setting, index) => validatePurchaseSettings(setting, codes[codeFields[index][0]], index === 3));
      if (!alive.current) return;
      const saved = await api.create({ ...codes, indent_id: Number(indentID) });
      if (alive.current) onCreated(saved.id);
    } catch (e) {
      if (alive.current) setError(`${e.message} No purchase orders are created by this step. If the response was lost, refresh history before creating again.`);
    } finally {
      lock.current = false;
      if (alive.current) { setBusy(false); onBusy(false); }
    }
  }

  return <section className="pqc-panel"><h2>New comparison</h2>
    <p>Choose your organisation’s configured purchase workflow. Codes are not inferred from their names. Only existing indent products and referenced vendor quotations can be compared.</p>
    {!choices.length && <p role="alert">No permitted transaction menus are available. Ask an administrator to configure the workflow and source view rights.</p>}
    <form onSubmit={create}><fieldset disabled={busy}><legend>Configured workflow</legend>
      <div className="pqc-form-grid">{codeFields.map(([key, title]) => <label key={key}>{title}
        <select required value={codes[key]} onChange={(event) => {
          setCodes({ ...codes, [key]: event.target.value }); setError("");
          if (key === "indent_code") { setIndentID(""); setIndents(null); }
        }}><option value="">Select configured code</option>{choices.filter((entry) => key === "order_code" || entry.rights?.can_view === true).map((entry) =>
          <option key={entry.menu_code} value={entry.menu_code}>{entry.display_title || entry.name} ({entry.menu_code})</option>)}</select>
      </label>)}</div>
      {indentCode && <section className="pqc-indent" aria-label="Select indent">
        <h3>Select an existing indent</h3>
        {loadError ? <><p role="alert">{loadError}</p><button type="button" onClick={() => setRetry((n) => n + 1)}>Retry indent list</button></> : !indents ? <p role="status">Loading indents…</p> : <>
          <label>Search indents<input type="search" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} /></label>
          <p>{filtered.length} matching indents. {selected ? `Selected: ${selected.tran_no || indentID} (ID ${indentID})` : "Select one indent below."}</p>
          {indents.some((row) => !Number.isSafeInteger(Number(row.tran_id)) || Number(row.tran_id) <= 0) && <p role="alert">Some rows cannot be selected: configure tran_id in the transaction head view fields.</p>}
          {!filtered.length && <p>No matching indents.</p>}
          <div className="pqc-indent-list">{filtered.slice((page - 1) * 20, page * 20).map((row, index) => <label key={`${row.tran_id}-${index}`}>
            <input type="radio" name="indent" value={row.tran_id || ""} checked={!!row.tran_id && indentID === String(row.tran_id)}
              disabled={!Number.isSafeInteger(Number(row.tran_id)) || Number(row.tran_id) <= 0} onChange={() => setIndentID(String(row.tran_id))} />
            <span><strong>{row.tran_no || `Indent #${row.tran_id || "unavailable"}`}</strong> · ID {text(row.tran_id)} · {text(row.date)} · {text(row.status)}</span>
          </label>)}</div>
          <Pager page={page} total={filtered.length} onChange={setPage} />
        </>}
      </section>}
      {error && <p role="alert">{error}</p>}
      <p>All four types must be non-stock transactions; the PO must use automatic numbering. The server validates configuration, permissions and source eligibility.</p>
      <button type="submit" className="pqc-primary" disabled={!allowed || !selected || busy}>{busy ? "Saving comparison…" : "Create comparison"}</button>
    </fieldset></form>
  </section>;
}

function VendorSummary({ vendors }) {
  return <div className="pqc-vendors">{vendors.map((vendor, index) => <div key={index}>
    <strong>{vendor.name} · ID {vendor.vendorID}</strong>
    <span>{vendor.currency}{vendor.exchangeRate !== undefined ? ` · exchange rate ${vendor.exchangeRate}` : ""}</span>
    <span>Selected base subtotal: {money(vendor.subtotal)}</span>
  </div>)}</div>;
}

function Offer({ offer, line, comparison, menus, quantity, onQuantity, editable }) {
  const key = offerKey(offer);
  const head = offer.head_data || {};
  const item = offer.item_data || {};
  const commercial = Object.entries({ ...head, ...item }).filter(([name]) => /tax|cgst|sgst|igst|discount|delivery|payment|terms|currency|exchange|freight/.test(name));
  return <article className={`pqc-offer ${offer.eligible ? "" : "pqc-unavailable"}`}>
    <h4>{head.company_name || `Vendor #${offer.vendor_id}`} <small>Vendor ID {offer.vendor_id}</small></h4>
    <DocumentLink menus={menus} code={comparison.quotation_code} id={offer.quotation_id}>Quotation {head.tran_no || offer.quotation_id} / line {offer.quotation_serial}</DocumentLink>
    <p>Enquiry #{offer.enquiry_id} · Rate <strong>{text(offer.rate)}</strong> {text(head.currency || (head.currency_id ? `currency ID ${head.currency_id}` : "Currency unspecified"))}</p>
    <dl className="pqc-quantities"><div><dt>Quoted</dt><dd>{offer.quantity}</dd></div><div><dt>Ordered</dt><dd>{offer.ordered_quantity}</dd></div><div><dt>Available</dt><dd>{offer.available_quantity}</dd></div></dl>
    <p className="pqc-badge">{offer.eligible ? "Eligible" : "Unavailable"}</p>
    {offer.unavailable_reason && <p>{offer.unavailable_reason}</p>}
    {commercial.length > 0 && <dl className="pqc-commercial">{commercial.map(([name, value]) => <div key={name}><dt>{label(name)}</dt><dd>{text(value)}</dd></div>)}</dl>}
    {editable && <div><label htmlFor={`pqc-quantity-${line.indent_serial}-${offer.quotation_id}-${offer.quotation_serial}`}>Order quantity — line {line.indent_serial}, offer {key}</label>
      <input id={`pqc-quantity-${line.indent_serial}-${offer.quotation_id}-${offer.quotation_serial}`} inputMode="decimal" autoComplete="off" value={quantity || ""} disabled={offer.eligible !== true || Number(offer.available_quantity) <= 0 || Number(line.remaining_quantity) <= 0}
        onChange={(event) => onQuantity(key, event.target.value)} aria-describedby={`pqc-offer-help-${line.indent_serial}-${offer.quotation_id}-${offer.quotation_serial}`} />
      <small id={`pqc-offer-help-${line.indent_serial}-${offer.quotation_id}-${offer.quotation_serial}`}>Leave blank to skip. Maximum {Math.min(Number(offer.available_quantity), Number(line.remaining_quantity))}; up to 6 decimal places.</small>
    </div>}
    <SourceData title="All quotation header details" data={head} />
    <SourceData title="All quotation item details" data={item} />
  </article>;
}

function ComparisonDetail({ id, menus, canAdd, onBusy, onOrdered }) {
  const [comparison, setComparison] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [quantities, setQuantities] = useState({});
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mustReload, setMustReload] = useState(false);
  const lock = useRef(false);
  const alive = useRef(true);
  const reviewHeading = useRef(null);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { if (review) reviewHeading.current?.focus(); }, [review]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setComparison(null); setError(""); setQuantities({}); setReview(false);
    api.get(id, controller.signal).then((data) => {
      if (!controller.signal.aborted) { setComparison(data); setMustReload(false); }
    }).catch((e) => { if (!controller.signal.aborted) setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [id, retry]);

  const validation = validatePurchaseSelections(comparison?.snapshot?.lines, quantities);
  const ordered = comparison?.status === "ordered";
  const sourceRights = comparison && [comparison.indent_code, comparison.enquiry_code, comparison.quotation_code].every((code) => hasPurchaseRight(menus, code, "can_view"));
  const canOrder = canAdd && sourceRights && hasPurchaseRight(menus, comparison?.order_code, "can_add");
  const editable = comparison?.status === "draft" && canOrder && !mustReload;

  async function generate() {
    if (lock.current || !review || !editable || validation.errors.length || !validation.selections.length) return;
    lock.current = true; setBusy(true); onBusy(true); setError("");
    try {
      const orders = await api.orders(id, validation.selections);
      if (!alive.current) return;
      // The POST returns orders, not the refreshed snapshot used to order. Never label
      // the pre-submit draft as the immutable audit snapshot if the follow-up GET fails.
      setComparison((current) => ({ ...current, status: "ordered", orders, snapshot: null }));
      setReview(false); setQuantities({}); setMustReload(true); onOrdered();
      const saved = await api.get(id);
      if (alive.current) { setComparison(saved); setMustReload(false); }
    } catch (e) {
      if (alive.current) {
        setError(`${e.message} Reload this comparison before continuing. A lost response may mean the orders were already saved; do not create a replacement comparison until checked.`);
        setMustReload(true); setReview(false);
      }
    } finally {
      lock.current = false;
      if (alive.current) { setBusy(false); onBusy(false); }
    }
  }

  return <section className="pqc-panel" aria-label="Comparison details" aria-busy={busy || loading}>
    <div className="pqc-heading"><h2>Comparison #{id}</h2><button disabled={busy || loading} onClick={() => setRetry((n) => n + 1)}>Reload comparison</button></div>
    {error && <p role="alert">{error}</p>}
    {loading && <p role="status">Loading comparison…</p>}
    {comparison && <>
      <p><span className="pqc-badge">{comparison.status}</span> · <DocumentLink menus={menus} code={comparison.indent_code} id={comparison.indent_id}>{comparison.indent_code} indent #{comparison.indent_id}</DocumentLink></p>
      <p>Enquiry: {comparison.enquiry_code} · Vendor quotation: {comparison.quotation_code} · Order: {comparison.order_code}</p>
      {ordered && <section className="pqc-result" aria-label="Generated purchase orders"><h3>Saved vendor purchase orders</h3>
        <p>Ordered comparisons are immutable. No edits, cancellation or repeat order generation are available here.</p>
        {(comparison.orders || []).map((order) => <article key={`${order.tran_code}-${order.tran_id}`}>
          <h4>Vendor {order.transaction?.company_data?.company_name || `#${order.vendor_id}`} · ID {order.vendor_id}</h4>
          <DocumentLink menus={menus} code={order.tran_code} id={order.tran_id}>Open {order.tran_code} {order.transaction?.head_data?.tran_no || `#${order.tran_id}`}</DocumentLink>
          <SourceData title="Saved order header" data={order.transaction?.head_data} />
          <SourceData title="Saved vendor details" data={order.transaction?.company_data} />
          {(order.transaction?.item_data || []).map((item, index) => <SourceData key={index} title={`Saved order line ${item.serial_no || index + 1}`} data={item} />)}
        </article>)}
      </section>}
      {!ordered && !canOrder && <p role="note">Ordering requires PQC add permission, source view permissions and add permission on {comparison.order_code}. The server is authoritative.</p>}
      {mustReload && <p role="alert">Ordering is locked until a successful reload. {ordered ? "Saved orders are shown above; the audit snapshot has not yet been reloaded." : "Quantities or permissions may have changed."}</p>}
      {comparison.snapshot && <>
        <h3>{ordered ? "Immutable snapshot used at ordering" : "Current indent lines and vendor offers"}</h3>
        <SourceData title="Indent header details" data={comparison.snapshot.indent} />
        {!comparison.snapshot.lines.length && <p>No indent lines are available.</p>}
        <fieldset disabled={busy || review}><legend>Vendor offer comparison</legend>
          {comparison.snapshot.lines.map((line) => <section className="pqc-line" key={line.indent_serial} aria-label={`Indent line ${line.indent_serial}`}>
            <h3>Line {line.indent_serial} · {line.item_data?.product_name || line.item_data?.description || `Product #${line.item_data?.product_id ?? "unknown"}`}</h3>
            <p>Product ID {text(line.item_data?.product_id)} · UOM {text(line.item_data?.uom)}</p>
            <dl className="pqc-quantities"><div><dt>Requested</dt><dd>{line.quantity}</dd></div><div><dt>Already ordered</dt><dd>{line.ordered_quantity}</dd></div><div><dt>Remaining</dt><dd>{line.remaining_quantity}</dd></div></dl>
            <SourceData title="Indent item details" data={line.item_data} />
            {!(line.offers || []).length ? <p>No referenced vendor offers for this line.</p> : <div className="pqc-offers">{line.offers.map((offer) => <Offer key={offerKey(offer)} offer={offer} line={line} comparison={comparison} menus={menus}
              quantity={quantities[offerKey(offer)]} onQuantity={(key, value) => setQuantities((current) => ({ ...current, [key]: value }))} editable={editable} />)}</div>}
          </section>)}
        </fieldset>
      </>}
      {editable && <section aria-label="Order selection summary">
        <h3>Vendor-wise selection</h3>
        <p>Base subtotals are quantity × quoted rate, not final PO totals. Taxes, discounts and supported amounts are calculated by the server. Different currencies/exchange rates are never added together here.</p>
        <VendorSummary vendors={validation.vendors} />
        {validation.errors.length > 0 && <ul role="alert">{validation.errors.map((message, index) => <li key={index}>{message}</li>)}</ul>}
        {!validation.selections.length && <p>Enter quantities on eligible offers to select them.</p>}
        {!review ? <button className="pqc-primary" disabled={busy || !validation.selections.length || !!validation.errors.length} onClick={() => setReview(true)}>Review vendor POs</button> :
          <section className="pqc-confirm" aria-label="Confirm purchase orders">
            <h3 tabIndex={-1} ref={reviewHeading}>Confirm vendor purchase orders</h3>
            <p>This will generate one PO per selected vendor and commit allocations. This screen cannot undo the orders. No stock is moved.</p>
            <ul>{validation.selections.map((selection) => <li key={offerKey(selection)}>Quotation {selection.quotation_id}, line {selection.quotation_serial}: quantity {selection.quantity}</li>)}</ul>
            <div className="pqc-actions"><button disabled={busy} onClick={() => setReview(false)}>Cancel</button><button className="pqc-primary" disabled={busy} onClick={generate}>{busy ? "Generating orders…" : "Confirm and generate vendor POs"}</button></div>
          </section>}
      </section>}
    </>}
  </section>;
}

export default function PurchaseComparisonsPage() {
  const [parents, setParents] = useState(readPurchaseMenus);
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const reload = () => setParents(readPurchaseMenus());
    window.addEventListener("storage", reload); window.addEventListener("sidebarItemsUpdated", reload);
    return () => { window.removeEventListener("storage", reload); window.removeEventListener("sidebarItemsUpdated", reload); };
  }, []);
  const menus = useMemo(() => activePurchaseMenus(parents), [parents]);
  const canView = hasPurchaseRight(menus, "PQC", "can_view");
  const canAdd = hasPurchaseRight(menus, "PQC", "can_add");
  const id = params.get("comparison") || "";
  const validID = /^[1-9]\d*$/.test(id) && Number.isSafeInteger(Number(id));
  const open = (value) => { setBusy(false); setParams(value ? { comparison: String(value) } : {}); };
  return <main className="pqc-page">
    <header className="pqc-header"><div><p>Purchase</p><h1>Purchase Comparison</h1><p>Compare referenced vendor quotations, split quantities and retain the order audit trail.</p></div>
      {canView && canAdd && id && <button disabled={busy} onClick={() => open("")}>New comparison</button>}
    </header>
    {!canView ? <section className="pqc-panel" role="alert"><h2>Purchase Comparison access required</h2><p>An active PQC menu and explicit view permission are required. Ask your administrator to grant access, then refresh your session. No permissions are granted by this screen.</p></section> : <>
      <p className="pqc-notice">Remaining quantities cover orders generated through this workflow only. Independently entered or historical POs are not reconciled automatically.</p>
      <div className="pqc-layout"><History onOpen={open} selectedID={id} refresh={refresh} disabled={busy} />
        <div>{id ? validID ? <ComparisonDetail key={id} id={id} menus={menus} canAdd={canAdd} onBusy={setBusy} onOrdered={() => setRefresh((n) => n + 1)} /> : <p role="alert">Invalid comparison ID. Open a saved comparison from history.</p> :
          canAdd ? <CreateComparison menus={menus} onBusy={setBusy} onCreated={(createdID) => { setRefresh((n) => n + 1); open(createdID); }} /> : <section className="pqc-panel"><h2>Read-only access</h2><p>Open a saved comparison from history. PQC add permission is required to create comparisons.</p></section>}</div>
      </div>
    </>}
  </main>;
}